/**
 * API Endpoints and Constants
 * This is the single source of truth for all API endpoints and hardcoded constants
 */

// API Base URLs (set from environment variables)
export const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5250';
export const ADMIN_API_BASE_URL = process.env.REACT_APP_ADMIN_API_URL || 'http://localhost:5250';
export const GRAPHQL_ENDPOINT = process.env.REACT_APP_GRAPHQL_URL || 'http://localhost:5000/graphql';

// ============================================================================
// Authentication Endpoints (Admin API)
// ============================================================================
export const AUTH_ENDPOINTS = {
  LOGIN: '/auth/login',
  REGISTER: '/auth/register',
  REFRESH_TOKEN: '/auth/refresh',
  LOGOUT: '/auth/logout',
  ME: '/auth/me',
  VERIFY_EMAIL: '/auth/verify-email',
  RESEND_VERIFICATION: '/auth/resend-verification',
  FORGOT_PASSWORD: '/auth/forgot-password',
  RESET_PASSWORD: '/auth/reset-password',
} as const;

// ============================================================================
// User Management Endpoints (Admin API)
// ============================================================================
export const USER_ENDPOINTS = {
  LIST: '/users',
  CREATE: '/users',
  GET_BY_ID: (id: string) => `/users/${id}`,
  UPDATE: (id: string) => `/users/${id}`,
  DELETE: (id: string) => `/users/${id}`,
  UPDATE_ROLES: (id: string) => `/users/${id}/roles`,
  RESET_PASSWORD: (id: string) => `/users/${id}/reset-password`,
  SEND_VERIFICATION: '/users/send-verification',
} as const;

// ============================================================================
// Role Management Endpoints (Admin API)
// ============================================================================
export const ROLE_ENDPOINTS = {
  LIST: '/roles',
  CREATE: '/roles',
  GET_BY_NAME: (name: string) => `/roles/${name}`,
  UPDATE: (name: string) => `/roles/${name}`,
  DELETE: (name: string) => `/roles/${name}`,
  GET_USERS_IN_ROLE: (name: string) => `/roles/${name}/users`,
} as const;


// ============================================================================
// Blog Endpoints (V1 API)
// ============================================================================
export const BLOG_ENDPOINTS = {
  LIST: '/blogs',
  CREATE: '/blogs',
  GET_BY_ID: (id: string) => `/blogs/${id}`,
  GET_BY_SLUG: (slug: string) => `/blogs/slug/${slug}`,
  UPDATE: (id: string) => `/blogs/${id}`,
  DELETE: (id: string) => `/blogs/${id}`,
  SEARCH: '/blogs/search',
  INCREMENT_VIEW: (id: string) => `/blogs/${id}/view`,
  TOGGLE_LIKE: (id: string) => `/blogs/${id}/like`,
  ADD_COMMENT: (id: string) => `/blogs/${id}/comments`,
  ADD_REPLY: (id: string, commentId: string) => `/blogs/${id}/comments/${commentId}/replies`,
  TOGGLE_COMMENT_LIKE: (id: string, commentId: string) => `/blogs/${id}/comments/${commentId}/like`,
  GET_EDITORS_PICK: '/blogs/editors-pick',
  SET_EDITORS_PICK: (id: string) => `/blogs/${id}/editors-pick`,
  UNSET_EDITORS_PICK: '/blogs/editors-pick',
  SET_FEATURED: (id: string) => `/blogs/${id}/featured`,
} as const;

// ============================================================================
// Project Endpoints (V1 API)
// ============================================================================
export const PROJECT_ENDPOINTS = {
  LIST: '/projects',
  CREATE: '/projects',
  GET_BY_ID: (id: string) => `/projects/${id}`,
  GET_BY_SLUG: (slug: string) => `/projects/slug/${slug}`,
  UPDATE: (id: string) => `/projects/${id}`,
  DELETE: (id: string) => `/projects/${id}`,
  SEARCH: '/projects/search',
  INCREMENT_VIEW: (id: string) => `/projects/${id}/view`,
  TOGGLE_LIKE: (id: string) => `/projects/${id}/like`,
  ADD_COMMENT: (id: string) => `/projects/${id}/comments`,
  ADD_REPLY: (id: string, commentId: string) => `/projects/${id}/comments/${commentId}/replies`,
  TOGGLE_COMMENT_LIKE: (id: string, commentId: string) => `/projects/${id}/comments/${commentId}/like`,
} as const;

// ============================================================================
// Contact Endpoints (V1 API)
// ============================================================================
export const CONTACT_ENDPOINTS = {
  SUBMIT: '/contact',
  LIST: '/contact',
  GET_BY_ID: (id: string) => `/contact/${id}`,
  MARK_AS_READ: (id: string) => `/contact/${id}/read`,
  DELETE: (id: string) => `/contact/${id}`,
} as const;

// ============================================================================
// Media Endpoints (V1 API)
// ============================================================================
export const MEDIA_ENDPOINTS = {
  LIST: '/media',
  UPLOAD: '/media',
  GET: (fileId: string) => `/media/${encodeURIComponent(fileId)}`,
  DELETE: (fileId: string) => `/media/${encodeURIComponent(fileId)}`,
  DOWNLOAD: (fileId: string) => `/media/${encodeURIComponent(fileId)}`,
} as const;

// ============================================================================
// Mailing List Endpoints (V1 API)
// ============================================================================
export const MAILING_LIST_ENDPOINTS = {
  SUBSCRIBE: '/mailing-list',
  LIST: '/mailing-list',
  GET_BY_EMAIL: (email: string) => `/mailing-list?email=${encodeURIComponent(email)}`,
  UPDATE_PREFERENCES: (id: string) => `/mailing-list/${id}/preferences`,
  UNSUBSCRIBE: (id: string) => `/mailing-list/${id}/unsubscribe`,
} as const;

// ============================================================================
// Taxonomy Endpoints (V1 API)
// ============================================================================
export const TAXONOMY_ENDPOINTS = {
  LIST: '/taxonomies',
  CREATE: '/taxonomies',
  GET_BY_ID: (id: string) => `/taxonomies/${id}`,
  UPDATE: (id: string) => `/taxonomies/${id}`,
  DELETE: (id: string) => `/taxonomies/${id}`,
  GET_BY_TYPE: (type: string) => `/taxonomies/type/${type}`,
  GET_BY_SLUG: (slug: string, type: string) => `/taxonomies/${slug}?type=${type}`,
} as const;

// ============================================================================
// Profile Endpoints (V1 API)
// ============================================================================
export const PROFILE_ENDPOINTS = {
  GET: '/profile',
  UPDATE: '/profile',
} as const;

// ============================================================================
// Health Check Endpoints (V1 API)
// ============================================================================
export const HEALTH_ENDPOINTS = {
  LIVE: '/health/live',
  READY: '/health/ready',
} as const;

// ============================================================================
// Pagination Configuration
// ============================================================================
export const PAGINATION = {
  DEFAULT_PAGE: 1,
  DEFAULT_PAGE_SIZE: 10,
  MAX_PAGE_SIZE: 100,
  MIN_PAGE_SIZE: 1,
  ADMIN_DEFAULT_PAGE_SIZE: 50,
  MEDIA_DEFAULT_PAGE_SIZE: 50,
} as const;

// ============================================================================
// Cache Configuration
// ============================================================================
export const CACHE = {
  BLOG_LIST_TTL_MS: 5 * 60 * 1000, // 5 minutes
  BLOG_DETAIL_TTL_MS: 10 * 60 * 1000, // 10 minutes
  PROJECT_LIST_TTL_MS: 5 * 60 * 1000, // 5 minutes
  PROJECT_DETAIL_TTL_MS: 10 * 60 * 1000, // 10 minutes
  PROFILE_TTL_MS: 15 * 60 * 1000, // 15 minutes
  TAXONOMIES_TTL_MS: 30 * 60 * 1000, // 30 minutes
} as const;

// ============================================================================
// Error Messages
// ============================================================================
export const ERROR_MESSAGES = {
  NETWORK_ERROR: 'Network error. Please check your connection.',
  SERVER_ERROR: 'Server error. Please try again later.',
  UNAUTHORIZED: 'You are not authorized to perform this action.',
  FORBIDDEN: 'Access denied.',
  NOT_FOUND: 'Resource not found.',
  VALIDATION_ERROR: 'Please check your input and try again.',
  UNKNOWN_ERROR: 'An unexpected error occurred.',
} as const;

// ============================================================================
// Success Messages
// ============================================================================
export const SUCCESS_MESSAGES = {
  CREATED: 'Successfully created.',
  UPDATED: 'Successfully updated.',
  DELETED: 'Successfully deleted.',
  SAVED: 'Successfully saved.',
  SUBMITTED: 'Successfully submitted.',
  UPLOADED: 'Successfully uploaded.',
} as const;

// ============================================================================
// Timeout Configuration (in milliseconds)
// ============================================================================
export const TIMEOUTS = {
  SHORT: 3000, // 3 seconds
  MEDIUM: 5000, // 5 seconds
  LONG: 10000, // 10 seconds
  API_REQUEST: 30000, // 30 seconds
} as const;

// ============================================================================
// Environment-specific settings
// ============================================================================
export const ENV_CONFIG = {
  IS_DEVELOPMENT: process.env.NODE_ENV === 'development',
  IS_PRODUCTION: process.env.NODE_ENV === 'production',
  IS_STAGING: process.env.REACT_APP_ENV === 'staging',
} as const;

// ============================================================================
// Logging Endpoints (Admin API)
// ============================================================================
export const LOGGING_ENDPOINTS = {
  SEND_LOG: '/logs',
  SEND_BATCH_LOGS: '/logs/batch',
} as const;

// ============================================================================
// Keep-Alive Endpoints (Admin API)
// ============================================================================
export const KEEP_ALIVE_ENDPOINTS = {
  STATUS: '/keep-alive',
} as const;

// ============================================================================
// ============================================================================
export const STORAGE_KEYS = {
  AUTH_TOKEN: 'auth_token',
  REFRESH_TOKEN: 'refresh_token',
  USER: 'user',
  THEME: 'theme_preference',
  LANGUAGE: 'language_preference',
  LAST_VISITED: 'last_visited_page',
} as const;

// ============================================================================
// Feature Flags
// ============================================================================
export const FEATURES = {
  ENABLE_COMMENTS: true,
  ENABLE_LIKES: true,
  ENABLE_SHARING: true,
  ENABLE_ANALYTICS: true,
  ENABLE_CONTACT_FORM: true,
  ENABLE_MAILING_LIST: true,
} as const;

// ============================================================================
// UI Configuration
// ============================================================================
export const UI_CONFIG = {
  ITEMS_PER_PAGE: 10,
  ADMIN_ITEMS_PER_PAGE: 20,
  MEDIA_GRID_COLUMNS: 3,
  TOAST_DURATION_MS: 5000,
  DEBOUNCE_DELAY_MS: 300,
  SEARCH_DEBOUNCE_MS: 500,
} as const;
